import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Search, 
  Link2, 
  FileText, 
  Wrench, 
  BarChart3,
  CreditCard
} from 'lucide-react';
import '../styles/CampanasPublicitariasPage.css';

const CampanasPublicitariasPage = () => {
  const [heroRef, heroInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [stepsRef, stepsInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [plansRef, plansInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [faqRef, faqInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  const processSteps = [
    {
      number: 1,
      icon: Search,
      title: 'Research',
      description: 'Se recolecta información sobre el modelo de negocio del cliente, ya sea en una reunión en vivo o a través de un documento en el que el cliente puede responder a preguntas pre seteadas.',
      details: [
        'Planteo de Buyer Persona',
        'Análisis de marca y de producto/servicio',
        'Historial publicitario',
        'Inversión posible'
      ]
    },
    {
      number: 2,
      icon: Link2,
      title: 'Conexión de Portafolios',
      description: 'En una videollamada o bien con tutoriales ya preparados, se conectan los portafolios comerciales de Meta y/o Google de nuestros profesionales con el del cliente.',
      details: [
        'Conexión de portafolios comerciales',
        'Compartir activos comerciales (fanpage, IG, dominio web)',
        'Instalación de píxel de Meta',
        'Configuración de Tag Manager de Google',
        'Seteo de eventos de conversión'
      ]
    },
    {
      number: 3,
      icon: FileText,
      title: 'Planteo de Estrategia Publicitaria',
      description: 'Se diseña el documento con la estrategia publicitaria propuesta y se entrega al cliente para revisión.',
      details: [
        'Definición de campañas',
        'Conjuntos de anuncios (presupuestos y segmentaciones)',
        'Diseño de anuncios (creatividades)',
        'Revisión con el cliente'
      ]
    },
    {
      number: 4,
      icon: Wrench,
      title: 'Montaje',
      description: 'Diseño/edición de creatividades en imagen y video. Se proveerán guiones para que el cliente pueda grabar a cámara si fuese necesario.',
      details: [
        'Diseño y edición de creatividades',
        'Videos y fotos en crudo (si es necesario)',
        'Guiones para grabación a cámara',
        'Montaje en Ads Manager del cliente (Meta y/o Google)'
      ],
      highlight: 'Aquí la campaña comienza a correr'
    },
    {
      number: 5,
      icon: BarChart3,
      title: 'Análisis y Entrega de Métricas',
      description: 'Las métricas tenidas en cuenta variarán según los objetivos de cada campaña. Se entrega reporte en PDF o Excel de manera semanal.',
      details: [
        'Reporte semanal en PDF o Excel',
        'Seguimiento del estado de la inversión',
        'Análisis de métricas clave',
        'Mejoras basadas en resultados obtenidos'
      ]
    }
  ];

  const plans = [
    {
      id: 1,
      name: 'Plan Low',
      price: 180,
      description: '1 campaña en una sola plataforma (Meta o Google) con 1 conjunto de anuncios (1 solo público objetivo) y de 4 a 7 anuncios.',
      features: [
        '1 campaña en Meta o Google',
        '1 conjunto de anuncios',
        '1 público objetivo',
        '4 a 7 anuncios',
        'Reporte mensual',
        'Soporte por WhatsApp'
      ],
      color: '#6B73FF',
      popular: false
    },
    {
      id: 2,
      name: 'Plan Mid',
      price: 240,
      description: '2 campañas en una sola plataforma (Meta o Google) o bien una en cada una, con 1 conjunto de anuncios cada una.',
      features: [
        '2 campañas (Meta y/o Google)',
        '1 conjunto por campaña',
        '2 públicos objetivos',
        '8 a 14 anuncios en total',
        'Reporte mensual',
        'Soporte por WhatsApp',
        'Optimización continua'
      ],
      color: '#f9a550',
      popular: true
    },
    {
      id: 3,
      name: 'Plan High',
      price: 420,
      description: '4 campañas en una plataforma o en ambas (Meta o Google), con hasta 4 conjuntos de anuncios.',
      features: [
        '4 campañas (Meta y/o Google)',
        'Hasta 4 conjuntos de anuncios',
        'Múltiples públicos objetivos',
        '16 a 28 anuncios en total',
        'Reporte mensual detallado',
        'Soporte por WhatsApp',
        'Optimización avanzada',
        'Análisis de competencia'
      ],
      color: '#8B5CF6',
      popular: false
    }
  ];

  const faqData = [
    {
      question: '¿Cuál es la diferencia entre los planes Low, Mid y High?',
      answer: 'La principal diferencia está en la cantidad de campañas, conjuntos de anuncios (públicos objetivos) y plataformas que se gestionan.\n\n– Low: 1 campaña en una sola plataforma. 4 a 7 creativos.\n– Mid: 2 campañas (en una o en ambas plataformas). 8 a 14 creativos.\n– High: 4 campañas, en Meta y/o Google, con varios públicos. 16 a 28 creativos.\nLa cantidad de campañas que necesita dependerá del modelo de negocio. Conctáctenos para recibir asesoramiento gratuito.'
    },
    {
      question: '¿El costo mensual incluye la inversión publicitaria?',
      answer: 'No. Los valores indicados corresponden al fee del equipo profesional.\n\nLa inversión en publicidad es un costo adicional, que el cliente paga directamente a Meta y/o Google desde su cuenta publicitaria.'
    },
    {
      question: '¿Cuánto debo invertir en publicidad?',
      answer: 'Depende de los objetivos, pero se recomienda:\n\n– USD $1 (mínimo) por día si buscás tráfico o alcance de marca.\n– USD $2 (mínimo) por día si buscás ventas o generación de clientes potenciales.\n\nCuanto mayor la inversión, mayor el alcance y los datos disponibles para optimizar.'
    },
    {
      question: '¿Cuándo empieza a correr mi campaña?',
      answer: 'Luego del Paso 4 (Montaje), una vez que las creatividades estén listas y las configuraciones aprobadas.\n\nDesde ese momento, la campaña ya está activa en tu cuenta.'
    },
    {
      question: '¿Qué métricas se reportan?',
      answer: 'Depende del objetivo de la campaña, pero usualmente se incluyen:\n\n– Alcance\n– Impresiones\n– CTR (Click Through Rate)\n– CPC (Costo por clic)\n– CPA (Costo por adquisición o cliente potencial)\n– ROAS (Retorno sobre la inversión publicitaria)'
    },
    {
      question: '¿Cada cuánto se entregan los reportes?',
      answer: 'Los reportes se entregan una vez por mes, en formato PDF o Excel.\n\nIncluyen análisis y sugerencias de optimización.'
    },
    {
      question: '¿Qué pasa si no tengo material gráfico o videos?',
      answer: 'Puedes enviarnos fotos o videos en crudo y nuestro equipo los edita.\n\nTambién te enviaremos guiones EGC (Employee Generated Content) para que tu o tu equipo puedan grabarse fácilmente a cámara.\n\nY guiones UGC (User Generated Content) para que un creador de contenido —tuyo o contratado— pueda grabar piezas que conecten con tu público.'
    },
    {
      question: '¿Qué pasa si me demoro en enviar materiales o accesos?',
      answer: 'Las demoras en la entrega de material pueden retrasar el inicio o rendimiento del servicio, y Runamatic no se hará responsable por esos tiempos.'
    },
    {
      question: '¿Cómo se realizan los pagos?',
      answer: 'Los pagos se realizan a través de Stripe con los métodos de pago disponibles para su país.'
    }
  ];

  const handleContactClick = () => {
    window.open('https://wa.me/5491123428342?text=Hola%2C%20quiero%20mas%20informaci%C3%B3n%20sobre%20Runamatic', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="campanas-page">
      {/* Hero Section */}
      <section className="ads-hero-section gradient-hero" ref={heroRef}>
        <div className="hero-background">
          <div className="hero-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>
        </div>

        <div className="container">
          <motion.div 
            className="ads-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="ads-hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Campañas Publicitarias{' '}
              <span className="text-gradient">Profesionales</span>
            </motion.h1>

            <motion.p 
              className="ads-hero-description"
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Potencia tu negocio con campañas publicitarias diseñadas por profesionales en Meta y Google Ads. 
              Nuestro equipo se encarga de todo: desde el análisis inicial hasta la optimización continua de tus anuncios.
            </motion.p>

            <motion.div 
              className="ads-hero-buttons"
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.button
                className="btn btn-primary btn-large"
                onClick={handleContactClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Comenzar Ahora
              </motion.button>
              
              <motion.button
                className="btn btn-outline btn-large"
                onClick={() => {
                  const element = document.getElementById('ads-plans');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Ver Planes
              </motion.button>
            </motion.div>

            <motion.div 
              className="ads-hero-stats"
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <div className="stat">
                <div className="stat-number">Meta</div>
                <div className="stat-label">Facebook & Instagram</div>
              </div>
              <div className="stat">
                <div className="stat-number">Google</div>
                <div className="stat-label">Ads & Display</div>
              </div>
              <div className="stat">
                <div className="stat-number">Reportes</div>
                <div className="stat-label">Entrega de métricas</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section id="ads-process" className="ads-process-section section" ref={stepsRef}>
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={stepsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2><span className="text-gradient">Nuestro Proceso</span></h2>
            <p>
              Seguimos un proceso estructurado y profesional para garantizar el éxito de tus campañas publicitarias
            </p>
          </motion.div>

          <div className="process-steps-grid">
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <motion.div
                  key={step.number}
                  className="process-step-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={stepsInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                >
                  <div className="step-number-wrapper">
                    <div className="step-number-badge">
                      <span>{step.number}</span>
                    </div>
                  </div>
                  
                  <div className="step-content-wrapper">
                    <div className="step-header">
                      <div className="step-icon">
                        <IconComponent size={24} />
                      </div>
                      <h3 className="step-title">{step.title}</h3>
                    </div>

                    <p className="step-description">{step.description}</p>

                    {step.details && (
                      <ul className="step-details">
                        {step.details.map((detail, idx) => (
                          <li key={idx}>
                            <span className="detail-check">✓</span>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    )}

                    {step.highlight && (
                      <div className="step-highlight">
                        💡 {step.highlight}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section id="ads-plans" className="ads-plans-section section" ref={plansRef}>
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={plansInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2><span className="text-gradient">Planes Publicitarios</span></h2>
            <p>
              Elige el plan que mejor se adapte a tus necesidades y presupuesto. 
              Si necesitas una estructura personalizada, podemos crear un budget a medida.
            </p>
          </motion.div>

          <div className="ads-plans-grid">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                className={`ads-plan-card ${plan.popular ? 'popular' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                animate={plansInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {plan.popular && (
                  <div className="popular-badge">
                    <span>✨ Más Popular</span>
                  </div>
                )}

                <div className="plan-header">
                  <div 
                    className="plan-icon"
                    style={{ backgroundColor: plan.color }}
                  >
                    {plan.name.split(' ')[1]}
                  </div>
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="plan-pricing">
                  <div className="price-container">
                    <span className="currency">USD $</span>
                    <span className="price">{plan.price}</span>
                    <span className="period">/mes</span>
                  </div>
                </div>

                <div className="plan-features">
                  <h4>Incluye:</h4>
                  <ul className="features-list">
                    {plan.features.map((feature, featureIndex) => (
                      <motion.li 
                        key={featureIndex}
                        initial={{ opacity: 0, x: -20 }}
                        animate={plansInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ 
                          duration: 0.4, 
                          delay: (index * 0.1) + (featureIndex * 0.02) 
                        }}
                      >
                        <span className="feature-check">✓</span>
                        {feature}
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <div className="plan-action">
                  <motion.button
                    onClick={handleContactClick}
                    className={`btn subscribe-btn ${plan.popular ? 'subscribe-btn-popular' : 'subscribe-btn-secondary'}`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="subscribe-icon">
                      <CreditCard size={16} />
                    </span>
                    Más Información
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Información adicional */}
          <motion.div 
            className="ads-info-section"
            initial={{ opacity: 0, y: 30 }}
            animate={plansInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="info-card">
              <h3>📌 A tener en cuenta</h3>
              <ul>
                <li>El cliente deberá sumar la inversión publicitaria directa a pagar a Meta y/o Google, que será debitada del método de pago asignado a su cuenta publicitaria.</li>
                <li>💡 Sugerencia de inversión mínima: USD $1 por día (tráfico, alcance de marca) o USD $2 por día (ventas, clientes potenciales)</li>
                <li>La disponibilidad de nuestro equipo será en horario comercial, de lunes a viernes.</li>
                <li>Se requiere el pago para comenzar a gestionar sus campañas.</li>
                <li>Los pagos se realizan al comienzo de cada mes.</li>
                <li>Para un servicio fluido, se recomienda respetar los tiempos de entrega de material en foto/video, si se requiere.</li>
                <li>Runamatic no se hará responsable por demoras ocasionadas por entregas fuera de término.</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="ads-faq" className="ads-faq-section section" ref={faqRef}>
        <div className="faq-background">
          <div className="faq-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
          </div>
        </div>

        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2><span className="text-gradient">💬 Preguntas Frecuentes</span></h2>
            <p>
              Resolvemos las dudas más comunes sobre nuestros servicios de campañas publicitarias
            </p>
          </motion.div>

          <div className="faq-grid">
            {faqData.map((item, index) => (
              <motion.div
                key={index}
                className={`faq-item ${openQuestion === index ? 'active' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={faqInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <div 
                  className="faq-question"
                  onClick={() => toggleQuestion(index)}
                >
                  <h3>{item.question}</h3>
                  <div className={`faq-icon ${openQuestion === index ? 'rotated' : ''}`}>
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path 
                        d="M6 9L12 15L18 9" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                
                <motion.div 
                  className="faq-answer"
                  initial={false}
                  animate={{
                    height: openQuestion === index ? "auto" : 0,
                    opacity: openQuestion === index ? 1 : 0
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut"
                  }}
                >
                  <div className="faq-answer-content">
                    {item.answer.split('\n').map((paragraph, pIndex) => (
                      paragraph.trim() && (
                        <p key={pIndex}>{paragraph}</p>
                      )
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* CTA Final */}
          <motion.div 
            className="ads-final-cta"
            initial={{ opacity: 0, y: 30 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <h3>¿Listo para potenciar tu negocio?</h3>
            <p>Nuestro equipo está preparado para crear y optimizar tus campañas publicitarias</p>
            <motion.button
              className="btn btn-primary btn-large"
              onClick={handleContactClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contactar con el Equipo
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CampanasPublicitariasPage;

